import { useContext, useEffect } from "react"

import { DragSourceDataContext } from "shared/context/drag-and-drop/DragSourceDataContext"
import { useDroppable } from "shared/lib/drag-and-drop/useDroppable"
import { useEvent } from "shared/lib/useEvent"
import { useTimeout } from "shared/lib/useTimeout"
import { Floor } from "shared/types/Floor"
import { FloorTypename, RoomTypename } from "shared/types/Project"
import { DragSource, isDragSource } from "shared/types/drag-and-drop/DragSource"
import { DropTarget, isDropTarget } from "shared/types/drag-and-drop/DropTarget"

import { DragEndEvent } from "@dnd-kit/core"
import z from "zod"

export interface UseProjectPageTabDropTargetArgs {
  eventKey: string
  index: number
  onTabSelect: (eventKey: string) => void
  floor?: Floor
  label: string
  hoverDelay?: number
  enabled?: boolean
  acceptTypes: string[]
  directDropAcceptTypes: string[]
  onDrop?: (dragSource: DragSource, dropTarget: DropTarget) => void
}

/**
 * Makes a tab a drop target for rooms and product positions.
 * Automatically selects the tab after hovering for a delay period.
 */
export function useProjectPageTabDropTarget({
  eventKey,
  onTabSelect,
  onDrop,
  hoverDelay = 1000,
  enabled = true,
  index,
  floor,
  acceptTypes,
  directDropAcceptTypes = [RoomTypename]
}: UseProjectPageTabDropTargetArgs) {
  const { dragSource } = useContext(DragSourceDataContext)
  const timeout = useTimeout()

  const typenameSchema = z.object({ __typename: z.string() })

  const handleDrop = useEvent((event: DragEndEvent) => {
    if (
      isDragSource(dragSource, { dataSchema: typenameSchema }) &&
      isDropTarget(event.over?.data.current) &&
      directDropAcceptTypes.includes(dragSource.data.__typename)
    ) {
      onDrop?.(dragSource, event.over?.data.current)
    }
  })

  const { isOver, setNodeRef } = useDroppable({
    id: `project-tab-${eventKey}`,
    data: floor,
    meta: { eventKey, index },
    accept: acceptTypes,
    type: FloorTypename,
    disabled: !enabled,
    onDragEnd: handleDrop
  })

  const canDrop =
    isDragSource(dragSource, {
      dataSchema: z.object({ __typename: z.string() })
    }) && directDropAcceptTypes.includes(dragSource?.data?.__typename ?? "")

  useEffect(() => {
    if (!enabled) return

    if (isOver && !timeout.isStarted()) {
      timeout.start(hoverDelay, () => {
        onTabSelect(eventKey)
      })
    }

    if (!isOver && timeout.isStarted()) {
      timeout.clear()
    }
  }, [isOver, enabled, eventKey, onTabSelect, hoverDelay, timeout])

  return {
    dropRef: enabled ? setNodeRef : null,
    isOver,
    canDrop
  }
}
